import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import ProfessionalTimeline from './ProfessionalTimeline';
import TimelinePlayer from './TimelinePlayer';
import ExportPanel from './ExportPanel';
import SelectedClipsChat from './SelectedClipsChat';
import EnhancedChatInterface from './EnhancedChatInterface';
import MediaDragDropSystem from './MediaDragDropSystem';
import { useTimeline } from '../hooks/useTimeline';
import safeSandbox from '../services/safeSandbox';
import aiChatService from '../services/aiChatService';
import timelineStateManager from '../services/timelineStateManager';
import { 
  ArrowLeft,
  Send,
  Bot,
  User,
  Sparkles,
  Upload,
  Download,
  Settings,
  Zap,
  Code,
  PlayCircle,
  StopCircle,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Lightbulb,
  Target,
  Layers,
  FileVideo
} from 'lucide-react';
import { projectAPI, chatAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const AITimelineEditor = ({ project, onBack, user }) => {
  // Timeline state management
  const {
    clips,
    selectedClipIds,
    currentTime,
    duration,
    aspectRatio,
    isPlaying,
    isLoading,
    error,
    canUndo,
    canRedo,
    addClip,
    moveClip,
    removeClip,
    trimClip,
    splitClip,
    selectClips,
    changeAspectRatio,
    play,
    pause,
    seek,
    undo,
    redo,
    timelineAPI
  } = useTimeline(project?.clips || []);

  // Chat and AI state
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [aiCode, setAiCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [executionLog, setExecutionLog] = useState([]);
  
  // Project state
  const [projectData, setProjectData] = useState(project);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('chat'); // 'chat', 'code', 'assets'
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [showSelectedClipsChat, setShowSelectedClipsChat] = useState(false);
  const [useEnhancedChat, setUseEnhancedChat] = useState(true); // Toggle for enhanced chat interface
  
  const chatEndRef = useRef(null);
  const codeExecutionRef = useRef(null);
  const { toast } = useToast();

  // Format time helper function
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  }, []);

  // AI-powered editing suggestions
  const intelligentSuggestions = [
    {
      icon: Target,
      title: "Create Hook",
      command: "Create a 30-second hook from the most engaging moments",
      code: `// Find high-energy clips
const energeticClips = timeline.findClipsByTag('action');
timeline.clear();
energeticClips.slice(0, 3).forEach((clip, i) => {
  timeline.addClip(clip, i * 10);
  timeline.trimClip(clip.id, clip.startTime, clip.startTime + 10);
});`
    },
    {
      icon: Layers,
      title: "Smart Sequence",
      command: "Arrange clips in an optimal storytelling sequence",
      code: `// Intelligent clip ordering
const talkingClips = timeline.findClipsByTag('talking');
const actionClips = timeline.findClipsByTag('action');
timeline.clear();

// Start with talking (setup)
talkingClips.slice(0, 2).forEach((clip, i) => {
  timeline.addClip(clip, i * clip.duration);
});

// Add action (climax)
let currentTime = talkingClips.length * 15;
actionClips.forEach((clip, i) => {
  timeline.addClip(clip, currentTime);
  currentTime += clip.duration;
});`
    },
    {
      icon: Zap,
      title: "Remove Silence",
      command: "Automatically remove silent sections to tighten the edit",
      code: `// Trim clips to remove silent parts
timeline.getClips().forEach(clip => {
  if (clip.tags.includes('talking')) {
    // Trim talking clips tighter
    const newDuration = clip.duration * 0.8;
    timeline.trimClip(clip.id, clip.startTime, clip.startTime + newDuration);
  }
});`
    },
    {
      icon: PlayCircle,
      title: "Pace Control",
      command: "Adjust pacing by analyzing content and motion",
      code: `// Dynamic pacing based on content
timeline.getClips().forEach(clip => {
  if (clip.tags.includes('action')) {
    // Keep action clips full length
    return;
  } else if (clip.tags.includes('talking')) {
    // Tighten talking sections
    const newDuration = Math.max(clip.duration * 0.7, 5);
    timeline.trimClip(clip.id, clip.startTime, clip.startTime + newDuration);
  }
});`
    }
  ];

  // Initialize timeline state management and load data
  useEffect(() => {
    if (project?.id) {
      // Initialize timeline state manager for this project
      timelineStateManager.initializeProject(project.id, project);
      
      loadChatHistory();
      loadProjectData();
      
      // Subscribe to state changes for enhanced features
      const unsubscribeStateUpdates = timelineStateManager.subscribe('timeline_updated', (data) => {
        console.debug('Timeline state updated:', data);
      });
      
      const unsubscribeConversation = timelineStateManager.subscribe('conversation_updated', (data) => {
        console.debug('Conversation updated:', data);
      });

      // Cleanup on unmount
      return () => {
        unsubscribeStateUpdates();
        unsubscribeConversation();
      };
    }
  }, [project]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Auto-save project changes and update state manager
  useEffect(() => {
    if (clips.length > 0 && projectData?.id) {
      // Update timeline state manager
      timelineStateManager.updateTimelineState({
        clips,
        duration,
        aspectRatio,
        selectedClipIds,
        currentTime
      }, 'timeline_change');

      const saveTimeout = setTimeout(() => {
        saveProject();
      }, 2000); // Auto-save after 2 seconds of inactivity

      return () => clearTimeout(saveTimeout);
    }
  }, [clips, aspectRatio, duration, selectedClipIds, currentTime]);

  // Auto-show selected clips chat for multi-selection
  useEffect(() => {
    if (selectedClipIds.length > 1) {
      setShowSelectedClipsChat(true);
    } else if (selectedClipIds.length === 0) {
      setShowSelectedClipsChat(false);
    }
  }, [selectedClipIds]);

  const loadChatHistory = async () => {
    try {
      if (project?.chat_session_id) {
        const history = await chatAPI.getChatHistory(project.id, project.chat_session_id);
        setChatMessages(history);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const loadProjectData = async () => {
    try {
      const updatedProject = await projectAPI.getProject(project.id);
      setProjectData(updatedProject);
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  };

  const saveProject = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const timelineState = {
        clips: clips.map(clip => ({
          ...clip,
          selected: undefined // Remove UI state
        })),
        duration,
        aspectRatio,
        currentTime
      };

      await projectAPI.updateProject(projectData.id, {
        timeline: timelineState,
        updated_at: new Date().toISOString()
      });

      setLastSaved(new Date());
      toast({
        title: "Project saved",
        description: "Timeline changes have been saved automatically.",
      });
    } catch (error) {
      console.error('Failed to save project:', error);
      toast({
        title: "Save failed",
        description: "Could not save timeline changes.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || isProcessingAI) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: newMessage,
      timestamp: new Date().toISOString()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsProcessingAI(true);

    try {
      // Get enhanced context from state manager
      const enhancedContext = timelineStateManager.getEnhancedContext();
      
      // Use enhanced AI chat service with state management context
      const timelineContext = {
        clips: clips.map(clip => ({
          id: clip.id,
          name: clip.name,
          duration: clip.duration,
          startTime: clip.startTime,
          tags: clip.tags || []
        })),
        duration,
        aspectRatio,
        selectedClipIds,
        // Add enhanced context from state manager
        conversationHistory: enhancedContext.recentConversation,
        editingPatterns: enhancedContext.editingPatterns,
        userPreferences: enhancedContext.userPreferences
      };

      const response = await aiChatService.processMessage(
        newMessage,
        timelineContext,
        project.id,
        project.chat_session_id
      );

      // Record conversation in state manager
      timelineStateManager.addConversationMessage(newMessage, response, {
        source: 'main_chat',
        confidence: response.metadata?.confidence
      });

      setChatMessages(prev => [...prev, response]);

      // If AI provided executable code, show it
      if (response.code) {
        setAiCode(response.code);
        setShowCode(true);
      }

    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessingAI(false);
    }
  };

  const executeAICode = useCallback(async () => {
    if (!aiCode.trim()) return;

    setExecutionLog([]);
    
    try {
      // Use safe sandbox for code execution
      const timelineContext = {
        clips: clips.map(clip => ({
          id: clip.id,
          name: clip.name,
          duration: clip.duration,
          startTime: clip.startTime,
          tags: clip.tags || []
        })),
        duration,
        aspectRatio,
        selectedClipIds
      };

      const result = await safeSandbox.executeTimelineCode(aiCode, timelineContext);

      setExecutionLog(result.logs || []);

      if (result.success) {
        // Record execution in both services
        aiChatService.recordCodeExecution(aiCode, result, result.executionTime);
        timelineStateManager.recordCodeExecution(aiCode, result, result.executionTime);

        toast({
          title: "Code executed successfully",
          description: `Execution completed in ${result.executionTime}ms`,
        });
      } else {
        toast({
          title: "Execution failed",
          description: result.error,
          variant: "destructive"
        });
      }

    } catch (error) {
      setExecutionLog(prev => [...prev, { 
        type: 'error', 
        message: `Execution error: ${error.message}` 
      }]);
      
      toast({
        title: "Execution failed",
        description: error.message,
        variant: "destructive"
      });
    }
  }, [aiCode, clips, duration, aspectRatio, selectedClipIds, toast]);

  const handleSelectedClipsChat = useCallback(async (message, context) => {
    try {
      // Enhanced timeline context with selection information
      const enhancedTimelineContext = {
        clips: clips.map(clip => ({
          id: clip.id,
          name: clip.name,
          duration: clip.duration,
          startTime: clip.startTime,
          tags: clip.tags || [],
          isSelected: selectedClipIds.includes(clip.id)
        })),
        duration,
        aspectRatio,
        selectedClipIds,
        selectionContext: context.selectionContext
      };

      const response = await aiChatService.processMessage(
        message,
        enhancedTimelineContext,
        project.id,
        project.chat_session_id,
        { 
          mode: 'selected_clips',
          selectedClips: context.selectedClips 
        }
      );

      // Record selected clips conversation in state manager
      timelineStateManager.addConversationMessage(message, response, {
        source: 'selected_clips_chat',
        selectedClipCount: context.selectedClips?.length || 0,
        confidence: response.metadata?.confidence
      });

      // If AI provided executable code, show it
      if (response.code) {
        setAiCode(response.code);
        setShowCode(true);
        setActiveTab('code');
      }

      return response;

    } catch (error) {
      console.error('Selected clips chat error:', error);
      throw error;
    }
  }, [clips, duration, aspectRatio, selectedClipIds, project.id, project.chat_session_id]);

  // Calculate selection context for selected clips chat
  const selectionContext = useMemo(() => {
    if (selectedClipIds.length === 0) return null;
    
    const selectedClips = clips.filter(clip => selectedClipIds.includes(clip.id));
    const totalDuration = selectedClips.reduce((sum, clip) => sum + clip.duration, 0);
    const startTime = Math.min(...selectedClips.map(clip => clip.startTime));
    const endTime = Math.max(...selectedClips.map(clip => clip.startTime + clip.duration));
    const allTags = [...new Set(selectedClips.flatMap(clip => clip.tags || []))];
    
    return {
      clipCount: selectedClips.length,
      totalDuration,
      timeRange: { start: startTime, end: endTime },
      tags: allTags,
      clips: selectedClips.map(clip => ({
        id: clip.id,
        name: clip.name,
        duration: clip.duration,
        startTime: clip.startTime,
        tags: clip.tags || []
      }))
    };
  }, [clips, selectedClipIds]);

  const applySuggestion = useCallback((suggestion) => {
    setAiCode(suggestion.code);
    setShowCode(true);
    setNewMessage(suggestion.command);
  }, []);

  const handleClipUpload = useCallback(async (files) => {
    for (const file of files) {
      try {
        // Upload file and get metadata
        const uploadResponse = await projectAPI.uploadClip(projectData.id, file);
        
        // Add to timeline
        const newClip = {
          id: uploadResponse.clip_id,
          name: file.name,
          url: uploadResponse.url,
          duration: uploadResponse.duration || 10,
          tags: uploadResponse.tags || [],
          waveformData: uploadResponse.waveform || []
        };

        addClip(newClip);

        toast({
          title: "Clip added",
          description: `${file.name} has been added to the timeline.`,
        });

      } catch (error) {
        console.error('Upload failed:', error);
        toast({
          title: "Upload failed",
          description: `Could not upload ${file.name}`,
          variant: "destructive"
        });
      }
    }
  }, [projectData?.id, addClip, toast]);

  // Show export panel as overlay
  if (showExportPanel) {
    return (
      <div className="flex flex-col h-screen bg-zinc-900 text-white">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExportPanel(false)}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Editor
            </Button>
            
            <h1 className="text-xl font-bold">Export Video</h1>
          </div>
        </div>

        {/* Export Panel */}
        <div className="flex-1 flex items-center justify-center p-8">
          <ExportPanel
            timeline={{
              clips,
              duration,
              aspectRatio
            }}
            projectId={projectData?.id}
            onClose={() => setShowExportPanel(false)}
            className="max-w-2xl"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-zinc-900 text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-700">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div>
            <h1 className="text-xl font-bold">{projectData?.name || 'Untitled Project'}</h1>
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <span>{clips.length} clips</span>
              <span>•</span>
              <span>{Math.round(duration)}s</span>
              {lastSaved && (
                <>
                  <span>•</span>
                  <span>Saved {lastSaved.toLocaleTimeString()}</span>
                </>
              )}
              {isSaving && (
                <>
                  <span>•</span>
                  <RefreshCw className="w-3 h-3 animate-spin" />
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAnalytics(!showAnalytics)}
          >
            <Settings className="w-4 h-4 mr-2" />
            Analytics
          </Button>
          
          <Button 
            variant="default" 
            size="sm"
            onClick={() => setShowExportPanel(true)}
            disabled={clips.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Video Player */}
        <div className="flex-1 flex flex-col">
          <div className="p-4 relative">
            <TimelinePlayer
              clips={clips}
              currentTime={currentTime}
              duration={duration}
              isPlaying={isPlaying}
              onTimeUpdate={seek}
              aspectRatio={aspectRatio}
              className="w-full"
            />

            {/* Selected Clips Chat Overlay */}
            {showSelectedClipsChat && selectionContext && (
              <div className="absolute top-4 right-4 w-80 z-50">
                <SelectedClipsChat
                  selectedClips={selectionContext.clips}
                  selectionContext={selectionContext}
                  onChatMessage={handleSelectedClipsChat}
                  onCloseChat={() => setShowSelectedClipsChat(false)}
                  isProcessing={isProcessingAI}
                />
              </div>
            )}
          </div>

          {/* Professional Timeline with Drag & Drop */}
          <MediaDragDropSystem
            onDropMedia={handleClipUpload}
            targetArea="timeline"
            className="relative"
          >
            <ProfessionalTimeline
              duration={duration}
              currentTime={currentTime}
              onTimeChange={seek}
              onPlay={play}
              onPause={pause}
              isPlaying={isPlaying}
              clips={clips}
              onClipSelect={selectClips}
              onClipMove={moveClip}
              onClipTrim={trimClip}
              onClipSplit={splitClip}
              onClipDelete={removeClip}
              selectedClipIds={selectedClipIds}
              aspectRatio={aspectRatio}
              onAspectRatioChange={changeAspectRatio}
              onUndo={undo}
              onRedo={redo}
              canUndo={canUndo}
              canRedo={canRedo}
            />
          </MediaDragDropSystem>
        </div>

        {/* AI Chat Panel */}
        <div className="w-80 border-l border-zinc-700 flex flex-col">
          {useEnhancedChat ? (
            /* Enhanced Chat Interface */
            <div className="flex-1">
              <EnhancedChatInterface
                selectedClips={selectedClipIds.map(id => clips.find(c => c.id === id)).filter(Boolean)}
                selectionContext={selectionContext}
                timelineContext={{
                  clips,
                  duration,
                  aspectRatio,
                  selectedClipIds,
                  currentTime
                }}
                onChatMessage={async (message, context) => {
                  if (context.mode === 'selected_clips') {
                    return await handleSelectedClipsChat(message, context);
                  } else {
                    // Handle general chat
                    const userMessage = {
                      id: Date.now(),
                      role: 'user',
                      content: message,
                      timestamp: new Date().toISOString()
                    };

                    setChatMessages(prev => [...prev, userMessage]);

                    const enhancedContext = timelineStateManager.getEnhancedContext();
                    const timelineContext = {
                      clips: clips.map(clip => ({
                        id: clip.id,
                        name: clip.name,
                        duration: clip.duration,
                        startTime: clip.startTime,
                        tags: clip.tags || []
                      })),
                      duration,
                      aspectRatio,
                      selectedClipIds,
                      conversationHistory: enhancedContext.recentConversation,
                      editingPatterns: enhancedContext.editingPatterns,
                      userPreferences: enhancedContext.userPreferences
                    };

                    const response = await aiChatService.processMessage(
                      message,
                      timelineContext,
                      project.id,
                      project.chat_session_id
                    );

                    timelineStateManager.addConversationMessage(message, response, {
                      source: 'enhanced_chat',
                      confidence: response.metadata?.confidence
                    });

                    setChatMessages(prev => [...prev, response]);
                    return response;
                  }
                }}
                onCodeGenerated={(code, response) => {
                  setAiCode(code);
                  setShowCode(true);
                  setActiveTab('code');
                }}
                onExecuteCode={executeAICode}
                isProcessing={isProcessingAI}
                className="h-full"
              />
              
              {/* Toggle to Regular Chat */}
              <div className="p-2 border-t border-zinc-700">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setUseEnhancedChat(false)}
                  className="w-full text-xs text-gray-400 hover:text-white"
                >
                  Switch to Simple Chat
                </Button>
              </div>
            </div>
          ) : (
            /* Regular Chat Interface */
            <>
              {/* Panel Tabs */}
              <div className="flex border-b border-zinc-700">
                <Button
                  variant={activeTab === 'chat' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('chat')}
                  className="flex-1 rounded-none"
                >
                  <Bot className="w-4 h-4 mr-2" />
                  AI Chat
                </Button>
                <Button
                  variant={activeTab === 'code' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('code')}
                  className="flex-1 rounded-none"
                >
                  <Code className="w-4 h-4 mr-2" />
                  Code
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setUseEnhancedChat(true)}
                  className="px-2"
                  title="Switch to Enhanced Chat"
                >
                  <Sparkles className="w-3 h-3" />
                </Button>
              </div>

          {/* Chat Tab */}
          {activeTab === 'chat' && (
            <div className="flex flex-col flex-1">
              {/* Intelligent Suggestions */}
              <div className="p-3 border-b border-zinc-700">
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-medium">Smart Suggestions</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {intelligentSuggestions.map((suggestion, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => applySuggestion(suggestion)}
                      className="h-auto p-2 text-xs flex flex-col items-start"
                    >
                      <suggestion.icon className="w-3 h-3 mb-1" />
                      <span className="font-medium">{suggestion.title}</span>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Chat Messages */}
              <ScrollArea className="flex-1 p-3">
                <div className="space-y-3">
                  {chatMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs p-3 rounded-lg ${
                          message.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-zinc-700 text-gray-100'
                        }`}
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          {message.role === 'user' ? (
                            <User className="w-3 h-3" />
                          ) : (
                            <Bot className="w-3 h-3" />
                          )}
                          <span className="text-xs opacity-75">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-sm">{message.content}</p>
                        
                        {/* Intent Analysis Display */}
                        {message.intentAnalysis && (
                          <div className="mt-2 p-2 bg-zinc-800 rounded text-xs">
                            <div className="flex items-center space-x-2 mb-1">
                              <Target className="w-3 h-3 text-blue-400" />
                              <span className="text-blue-300">Intent Analysis</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1 text-gray-400">
                              <div>Goal: {message.intentAnalysis.primary_goal}</div>
                              <div>Complexity: {message.intentAnalysis.complexity}</div>
                              <div>Scope: {message.intentAnalysis.scope}</div>
                              <div>Confidence: {Math.round(message.intentAnalysis.confidence * 100)}%</div>
                            </div>
                            {message.intentAnalysis.operations && message.intentAnalysis.operations.length > 0 && (
                              <div className="mt-1">
                                <span className="text-blue-300">Operations: </span>
                                {message.intentAnalysis.operations.map((op, i) => (
                                  <span key={i} className="text-yellow-300 text-xs">
                                    {op.action}{i < message.intentAnalysis.operations.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Query Results Display */}
                        {message.queryResult && (
                          <div className="mt-2 p-3 bg-zinc-800 rounded">
                            <div className="flex items-center space-x-2 mb-2">
                              <Target className="w-4 h-4 text-green-400" />
                              <span className="text-green-300 text-sm font-medium">
                                Query Results ({message.queryResult.type})
                              </span>
                            </div>
                            
                            {message.queryResult.results && message.queryResult.results.length > 0 && (
                              <div className="space-y-2">
                                <div className="text-xs text-gray-400">
                                  Found {message.queryResult.count || message.queryResult.results.length} clips
                                </div>
                                <div className="max-h-32 overflow-y-auto space-y-1">
                                  {message.queryResult.results.slice(0, 5).map((clip, index) => (
                                    <div 
                                      key={clip.id} 
                                      className="flex items-center justify-between p-2 bg-zinc-700 rounded text-xs cursor-pointer hover:bg-zinc-600"
                                      onClick={() => {
                                        // Select the clip in timeline
                                        selectClips([clip.id]);
                                        // Seek to clip position
                                        seek(clip.startTime);
                                      }}
                                    >
                                      <div className="flex items-center space-x-2">
                                        <FileVideo className="w-3 h-3 text-blue-400" />
                                        <span className="text-white">{clip.name}</span>
                                      </div>
                                      <div className="flex items-center space-x-2 text-gray-400">
                                        <span>{formatTime(clip.duration)}</span>
                                        <span>@{formatTime(clip.startTime)}</span>
                                        {clip.engagementScore && (
                                          <span className="text-yellow-400">
                                            ⭐{Math.round(clip.engagementScore * 100)}%
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {message.queryResult.results.length > 5 && (
                                  <div className="text-xs text-gray-500">
                                    ...and {message.queryResult.results.length - 5} more clips
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {message.queryResult.analysis && (
                              <div className="mt-2 p-2 bg-zinc-700 rounded text-xs">
                                <div className="text-blue-300 mb-1">Analysis Results:</div>
                                <pre className="text-gray-300 whitespace-pre-wrap">
                                  {JSON.stringify(message.queryResult.analysis, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Smart Generation Metadata Display */}
                        {message.smartGeneration && (
                          <div className="mt-2 p-2 bg-zinc-800 rounded text-xs">
                            <div className="flex items-center space-x-2 mb-1">
                              <Sparkles className="w-3 h-3 text-purple-400" />
                              <span className="text-purple-300 text-xs">Smart Generation</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1 text-gray-400">
                              <div>Strategy: {message.smartGeneration.strategy}</div>
                              <div>Confidence: {Math.round(message.smartGeneration.promptConfidence * 100)}%</div>
                              <div>Complexity: {message.smartGeneration.codeComplexity}/10</div>
                              <div>Optimizations: {message.smartGeneration.optimizations?.length || 0}</div>
                            </div>
                            {message.smartGeneration.optimizations?.length > 0 && (
                              <div className="mt-1">
                                <span className="text-purple-300 text-xs">Applied: </span>
                                {message.smartGeneration.optimizations.map((opt, i) => (
                                  <span key={i} className="text-green-300 text-xs">
                                    {opt}{i < message.smartGeneration.optimizations.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {message.code && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setAiCode(message.code);
                              setShowCode(true);
                              setActiveTab('code');
                            }}
                            className="mt-2 text-xs"
                          >
                            <Code className="w-3 h-3 mr-1" />
                            View Code
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {isProcessingAI && (
                    <div className="flex justify-start">
                      <div className="bg-zinc-700 text-gray-100 p-3 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Bot className="w-3 h-3" />
                          <RefreshCw className="w-3 h-3 animate-spin" />
                          <span className="text-xs">AI is thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div ref={chatEndRef} />
              </ScrollArea>

              {/* Chat Input */}
              <div className="p-3 border-t border-zinc-700">
                <div className="flex space-x-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Describe your edit..."
                    onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                    disabled={isProcessingAI}
                    className="flex-1"
                  />
                  <Button
                    onClick={sendChatMessage}
                    disabled={isProcessingAI || !newMessage.trim()}
                    size="sm"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Code Tab */}
          {activeTab === 'code' && (
            <div className="flex flex-col flex-1">
              {/* Code Editor */}
              <div className="flex-1 p-3">
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Timeline Code</span>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={executeAICode}
                      disabled={!aiCode.trim()}
                    >
                      <PlayCircle className="w-4 h-4 mr-2" />
                      Execute
                    </Button>
                  </div>
                  <textarea
                    value={aiCode}
                    onChange={(e) => setAiCode(e.target.value)}
                    placeholder="// AI-generated timeline code will appear here
// You can also write custom timeline manipulation code

timeline.addClip(clip, startTime);
timeline.moveClip(clipId, newTime);
timeline.removeClip(clipId);"
                    className="w-full h-48 p-3 bg-zinc-800 border border-zinc-600 rounded text-sm font-mono resize-none"
                  />
                </div>

                {/* Execution Log */}
                {executionLog.length > 0 && (
                  <div className="border border-zinc-600 rounded">
                    <div className="p-2 bg-zinc-800 border-b border-zinc-600">
                      <span className="text-xs font-medium">Execution Log</span>
                    </div>
                    <div className="p-2 max-h-32 overflow-y-auto">
                      {executionLog.map((log, index) => (
                        <div key={index} className={`text-xs mb-1 ${
                          log.type === 'error' ? 'text-red-400' :
                          log.type === 'success' ? 'text-green-400' :
                          'text-gray-300'
                        }`}>
                          {log.type === 'error' && <AlertCircle className="w-3 h-3 inline mr-1" />}
                          {log.type === 'success' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                          {log.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* API Reference */}
              <div className="p-3 border-t border-zinc-700">
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium mb-2">Timeline API Reference</summary>
                  <div className="space-y-1 text-gray-400">
                    <div><code>timeline.addClip(clip, startTime)</code></div>
                    <div><code>timeline.moveClip(clipId, newTime)</code></div>
                    <div><code>timeline.removeClip(clipId)</code></div>
                    <div><code>timeline.getClips()</code></div>
                    <div><code>timeline.selectClips([clipIds])</code></div>
                    <div><code>timeline.findClipsByTag(tag)</code></div>
                    <div><code>timeline.splitClip(clipId, time)</code></div>
                    <div><code>timeline.trimClip(clipId, start, end)</code></div>
                  </div>
                </details>
              </div>
            </div>
          )}
            </>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-600 text-white">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AITimelineEditor;